import React, { useMemo, useRef } from 'react'
import classNames from 'classnames'
import { Category, SiteCodes } from 'shared-types'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Link } from '~/components'
import { CategoryTileProps } from '~/components/CategoryTile'
import CategoryTiles from '~/components/CategoryTiles'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useDebounce, usePage } from '~/hooks'
import { ProductListingCategoryTilesProps } from './ProductListing.types'
import styles from './ProductListingCategoryTiles.module.scss'

const PAGE_WIDTH_CHANGE_DELAY = 100

type TextTilesProps = {
  childCategories: Category[]
}

const TextTiles = ({ childCategories }: TextTilesProps) => {
  const ghostContainerRef = useRef<HTMLDivElement>(null)
  const { pageWidth } = usePage()
  const debouncedPageWidth = useDebounce(pageWidth, PAGE_WIDTH_CHANGE_DELAY)

  const containerStyle = useMemo(() => {
    const tilesContainer = ghostContainerRef.current

    if (!tilesContainer) {
      return null
    }

    const tiles = Array.from(tilesContainer.children)
    let maxHeight = 0
    let maxWidth = 0

    tiles.forEach((tile) => {
      const tileHeight = tile.getBoundingClientRect().height
      const tileWidth = tile.getBoundingClientRect().width

      if (tileHeight > maxHeight) {
        maxHeight = tileHeight
      }
      if (tileWidth > maxWidth) {
        maxWidth = tileWidth
      }
    })

    return {
      '--text-tile-width': `${maxWidth}px`,
      '--text-tile-height': `${maxHeight}px`,
    } as React.CSSProperties
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPageWidth, ghostContainerRef?.current])

  const renderTextTile = (childCategory: Category, index: number) => {
    return (
      <div
        key={index}
        className={classNames('px-3 py-2 w-1/2 md:w-auto', styles.textTile)}>
        <Link
          href={childCategory.url}
          className={classNames(
            'flex justify-center items-center h-full',
            'rounded-lg px-3 py-3 text-sm text-center',
            'bg-[#D9D9D9] hover:bg-black hover:text-white duration-200'
          )}>
          <Typography
            tag={TypographyTag.div}
            variant={TypographyVariant.BodyRegular}>
            {childCategory.name}
          </Typography>
        </Link>
      </div>
    )
  }

  const renderTextTiles = () => {
    return childCategories.map((childCategory, index) => {
      return renderTextTile(childCategory, index)
    })
  }

  const renderSwipableTextTiles = () => {
    return childCategories.map((childCategory, index) => {
      return (
        <SwiperSlide key={index} className='!w-min'>
          {renderTextTile(childCategory, index)}
        </SwiperSlide>
      )
    })
  }

  return (
    <div className='relative'>
      <div
        ref={ghostContainerRef}
        className='absolute left-0 right-0 flex flex-wrap invisible'>
        {renderTextTiles()}
      </div>
      <div
        style={containerStyle}
        className={classNames('-mx-3 block md:hidden', {
          invisible: containerStyle == null,
        })}>
        <Swiper slidesPerView='auto'>{renderSwipableTextTiles()}</Swiper>
      </div>
      <div
        style={containerStyle}
        className={classNames('-mx-3 flex-wrap hidden md:flex', {
          invisible: containerStyle == null,
        })}>
        {renderTextTiles()}
      </div>
    </div>
  )
}

const ProductListingCategoryTiles = ({
  category,
}: ProductListingCategoryTilesProps) => {
  const { siteCode } = usePage()
  const isRtm = siteCode === SiteCodes.RTM
  const areCategoriesAvailable = category?.children?.length > 0
  const childCategories = category?.children?.slice().sort((a, b) => {
    return parseFloat(a.orderHint) - parseFloat(b.orderHint)
  })

  if (!areCategoriesAvailable) {
    return null
  }

  const canShowBlockTiles = !isRtm || category?.tier === 1

  return (
    <div className='py-4 md:mt-2 bg-white'>
      {!canShowBlockTiles && <TextTiles childCategories={childCategories} />}
      {canShowBlockTiles && (
        <CategoryTiles
          tiles={childCategories.map((childCategory): CategoryTileProps => {
            return {
              title: childCategory.name,
              action: {
                title: childCategory.name,
                href: childCategory.url,
              },
              image: childCategory.thumbnail,
              size: isRtm ? 'xl' : 'lg',
              blendBackground: false,
              shape: isRtm ? 'square' : 'circle',
              backgroundColor: isRtm ? '#D9D9D9' : undefined,
              horizontallyCentered: false,
            }
          })}
        />
      )}
    </div>
  )
}

export { ProductListingCategoryTiles }
